import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileText, Copy, GraduationCap, Rocket, Save, RotateCcw, RefreshCw } from "lucide-react";

export function PromptsTab() {
  const [data, setData] = useState(null);
  const [persona, setPersona] = useState("undergraduate");
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState("");

  const reload = useCallback(() => {
    api.get("/admin/prompts").then(({ data }) => { setData(data); setEdits({}); }).catch(() => toast.error("Failed to load prompts"));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const copy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Copied to clipboard");
    } catch (e) {
      toast.error("Copy failed - please copy manually");
    }
  };

  const save = async (agent, current) => {
    const key = `${agent}|${persona}`;
    const text = (edits[key] ?? current).trim();
    if (!text) { toast.error("Page goal cannot be empty"); return; }
    setBusy(key);
    try {
      await api.put("/admin/prompts", { agent, persona, page_goal: text });
      toast.success("Prompt saved — used immediately");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setBusy(""); }
  };

  const reset = async (agent) => {
    const key = `${agent}|${persona}`;
    setBusy(key);
    try {
      await api.delete(`/admin/prompts/${agent}/${persona}`);
      toast.success("Restored default prompt");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally { setBusy(""); }
  };

  if (!data) return <div className="py-16 text-center text-muted-foreground">Loading prompts…</div>;

  return (
    <div className="space-y-5" data-testid="admin-prompts">
      <Card className="p-4 rounded-2xl border-border bg-navy/5">
        <p className="text-sm text-navy">{data.note}</p>
      </Card>

      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-navy">Student type:</span>
        {[{ k: "undergraduate", i: GraduationCap, l: "Undergraduate" }, { k: "postgraduate", i: Rocket, l: "Postgraduate" }].map((p) => {
          const Icon = p.i; const active = persona === p.k;
          return (
            <button key={p.k} type="button" data-testid={`prompt-persona-${p.k}`} onClick={() => { setPersona(p.k); setEdits({}); }}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active ? "bg-orange text-white" : "bg-white border border-border text-navy hover:border-navy/30"}`}>
              <Icon className="h-4 w-4" /> {p.l}
            </button>
          );
        })}
      </div>

      {data.agents.map((a) => {
        const p = a.personas[persona] || {};
        const key = `${a.agent}|${persona}`;
        const value = edits[key] ?? (p.page_goal || "");
        const dirty = edits[key] !== undefined && edits[key] !== (p.page_goal || "");
        const working = busy === key;
        return (
          <Card key={a.agent} className="p-6 rounded-2xl border-border" data-testid={`prompt-agent-${a.agent}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-orange" />
                <h3 className="font-display font-extrabold italic text-navy">{a.label}</h3>
                <Badge variant={a.engine === "chatbase" ? "default" : "secondary"} className={a.engine === "chatbase" ? "bg-navy text-white hover:bg-navy" : ""}>
                  {a.engine === "chatbase" ? "Chatbase agent" : "Emergent LLM"}
                </Badge>
                {p.overridden && <Badge className="bg-orange/15 text-orange hover:bg-orange/15" data-testid={`prompt-overridden-${a.agent}`}>Custom</Badge>}
              </div>
              <Button size="sm" variant="outline" className="rounded-full" data-testid={`prompt-copy-${a.agent}`} onClick={() => copy(p.system_message)}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy context
              </Button>
            </div>

            {p.editable ? (
              <div className="space-y-3">
                <div className="eyebrow text-[11px] text-orange">Page goal ({persona}) — editable</div>
                <Textarea
                  data-testid={`prompt-edit-${a.agent}`}
                  value={value}
                  onChange={(e) => setEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                  rows={5}
                  className="rounded-xl text-sm leading-relaxed"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" className="rounded-full bg-orange hover:bg-orange-dark text-white" data-testid={`prompt-save-${a.agent}`} disabled={working || !dirty} onClick={() => save(a.agent, p.page_goal || "")}>
                    {working ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" /> Save</>}
                  </Button>
                  {p.overridden && (
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={`prompt-reset-${a.agent}`} disabled={working} onClick={() => reset(a.agent)}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore default
                    </Button>
                  )}
                  {dirty && <span className="text-xs text-orange">Unsaved changes</span>}
                </div>
              </div>
            ) : (
              p.page_goal && (
                <div className="mb-3">
                  <div className="eyebrow text-[11px] text-orange mb-1">Page goal ({persona})</div>
                  <p className="text-sm text-navy">{p.page_goal}</p>
                </div>
              )
            )}

            <div className="eyebrow text-[11px] text-orange mt-4 mb-1">{a.engine === "chatbase" ? "Context sent to the agent (preview)" : "System message"}</div>
            <pre className="text-xs bg-navy text-white/90 rounded-xl p-4 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">{p.system_message}</pre>
          </Card>
        );
      })}
    </div>
  );
}
